import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Compass,
  ExternalLink,
  Link2,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Undo2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { api } from '@/lib/api'
import { ApproveModal, LinkModal, RejectModal } from '@/components/discovery/CandidateModals'
import { Candidate, CandidateTab, DiscoveryMeta, ListResponse, apiError } from '@/components/discovery/types'

const PAGE_SIZE = 50

type SortKey = 'published_desc' | 'seen_desc' | 'name_asc' | 'broker_asc'

const TABS: { key: CandidateTab; label: string; hint: string }[] = [
  { key: 'new', label: 'New', hint: 'Found after go-live and waiting for a decision.' },
  {
    key: 'backlog',
    label: 'Before go-live',
    hint: 'Already on the broker sites when discovery started. Not counted as new; any of them can still be approved.',
  },
  { key: 'snoozed', label: 'Snoozed', hint: 'Postponed by you, or automatically while no units are for sale yet.' },
  { key: 'rejected', label: 'Rejected', hint: 'Never proposed again. Resorts and branded residences are rejected automatically.' },
  { key: 'done', label: 'Done', hint: 'Approved as a project, or linked to an existing one.' },
  { key: 'gone', label: 'No longer listed', hint: 'Disappeared from the broker site before a decision was taken.' },
]

const FLAG_LABELS: Record<string, { label: string; tone: string }> = {
  'out_of_scope:resort_branded': { label: 'Resort / branded', tone: 'bg-secondary-200 text-secondary-700' },
  'doubt:plots': { label: 'Plots?', tone: 'bg-amber-100 text-amber-800' },
  'doubt:commercial': { label: 'Commercial?', tone: 'bg-amber-100 text-amber-800' },
  'doubt:senior': { label: 'Senior living?', tone: 'bg-amber-100 text-amber-800' },
  no_units: { label: 'No units', tone: 'bg-secondary-100 text-secondary-600' },
  pre_launch: { label: 'Pre-launch', tone: 'bg-blue-100 text-blue-800' },
  republish_pair: { label: 'Re-published?', tone: 'bg-purple-100 text-purple-800' },
}

const REASON_LABELS: Record<string, string> = {
  baseline: 'Existed before go-live',
  bulk_discovery: 'Unusually large batch',
  out_of_scope: 'Out of scope',
  no_units: 'No units for sale',
  sold_out: 'Sold out',
  not_residential: 'Not residential',
  duplicate: 'Duplicate',
  manual: 'Postponed',
  approved: 'Approved',
  linked: 'Linked',
  already_tracked: 'Already a project',
  units_available: 'Units available',
  relisted: 'Listed again',
  project_deleted: 'Project deleted',
  snooze_expired: 'Snooze ended',
  reopened: 'Reopened',
  other: 'Other',
}

// Date-only values (snoozed_until) are calendar days: parse them as local, not as UTC midnight.
const safeDate = (value: string | null | undefined, pattern = 'dd MMM yyyy') => {
  if (!value) return '—'
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseISO(value) : new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : format(d, pattern)
}

const relative = (value: string | null | undefined) => {
  if (!value) return 'never'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? 'never' : `${formatDistanceToNow(d)} ago`
}

export function Discovery() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<CandidateTab>('new')
  const [page, setPage] = useState(1)
  const [brokerId, setBrokerId] = useState<number | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortKey>('published_desc')
  const [onlyLive, setOnlyLive] = useState(false)
  const [onlyDuplicates, setOnlyDuplicates] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [showSources, setShowSources] = useState(false)
  const [approving, setApproving] = useState<Candidate | null>(null)
  const [linking, setLinking] = useState<Candidate | null>(null)
  const [rejecting, setRejecting] = useState<Candidate[] | null>(null)

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [searchInput])

  const meta = useQuery<DiscoveryMeta>({
    queryKey: ['discovery-meta'],
    queryFn: async () => (await api.get('/discovery/meta')).data,
    staleTime: 30_000,
    refetchInterval: 120_000,
  })

  const list = useQuery<ListResponse>({
    queryKey: ['discovery-candidates', tab, page, brokerId, search, sort, onlyLive, onlyDuplicates],
    queryFn: async () => {
      const params: Record<string, unknown> = { tab, page, limit: PAGE_SIZE, sort }
      if (brokerId) params.broker_id = brokerId
      if (search) params.q = search
      if (onlyLive) params.live = true
      if (onlyDuplicates) params.has_match = true
      return (await api.get('/discovery/candidates', { params })).data
    },
    keepPreviousData: true,
  })

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['discovery-candidates'] })
    queryClient.invalidateQueries({ queryKey: ['discovery-meta'] })
    queryClient.invalidateQueries({ queryKey: ['discovery-meta-badge'] })
    setSelected(new Set())
  }

  // A selection belongs to the page/filters it was made on.
  useEffect(() => {
    setSelected(new Set())
  }, [tab, page, brokerId, search, sort, onlyLive, onlyDuplicates])

  const runMutation = useMutation({
    mutationFn: async (args: { baseline: boolean; hosts?: string[] }) => (await api.post('/discovery/run', args)).data,
    onSuccess: (data) => {
      const c = data?.totals ?? {}
      const message = `Discovery ${data.status}: ${c.created ?? 0} new, ${c.gone ?? 0} gone, ${c.failedHosts ?? 0} of ${c.hosts ?? 0} sources failed`
      if (data.status === 'failed') toast.error(message)
      else if (data.status === 'partial') toast(message, { icon: '⚠️' })
      else toast.success(message)
      refreshAll()
    },
    onError: (error) => toast.error(apiError(error).message),
  })

  const actionMutation = useMutation({
    mutationFn: async (args: { id: number; action: 'snooze' | 'reopen' }) =>
      (await api.post(`/discovery/candidates/${args.id}/${args.action}`, {})).data,
    onSuccess: (_d, vars) => {
      toast.success(vars.action === 'snooze' ? 'Snoozed for 30 days' : 'Moved back to New')
      refreshAll()
    },
    onError: (error) => toast.error(apiError(error).message),
  })

  const bulkMutation = useMutation({
    mutationFn: async (action: 'snooze' | 'reopen') =>
      (await api.post('/discovery/candidates/bulk', { ids: selectedItems.map((c) => c.candidate_id), action })).data,
    onSuccess: (data) => {
      toast.success(`${data.changed} of ${data.requested} updated`)
      refreshAll()
    },
    onError: (error) => toast.error(apiError(error).message),
  })

  const items = list.data?.items ?? []
  const total = list.data?.total ?? 0

  // Acting on the last row of the last page leaves it empty: step back one page.
  useEffect(() => {
    if (page > 1 && !list.isFetching && list.data && list.data.items.length === 0) setPage((p) => Math.max(1, p - 1))
  }, [page, list.isFetching, list.data])
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const counts = meta.data?.counts
  const brokers = useMemo(() => {
    const seen = new Map<number, string>()
    for (const h of meta.data?.hosts ?? []) if (h.broker_name) seen.set(h.broker_id, h.broker_name)
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [meta.data])

  const countFor = (key: CandidateTab) => (counts ? (counts as any)[key] ?? 0 : undefined)
  const allOnPageSelected = items.length > 0 && items.every((c) => selected.has(c.candidate_id))
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) items.forEach((c) => next.delete(c.candidate_id))
      else items.forEach((c) => next.add(c.candidate_id))
      return next
    })
  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const lastRun = meta.data?.last_run
  const failedHosts = (meta.data?.hosts ?? []).filter((h) => h.last_error)
  const canApprove = (c: Candidate) => ['pending', 'snoozed', 'archived'].includes(c.review_status)
  const canReject = (c: Candidate) => ['pending', 'snoozed', 'archived', 'gone'].includes(c.review_status)
  const canReopen = (c: Candidate) => ['rejected', 'snoozed', 'archived', 'gone'].includes(c.review_status)

  const selectedItems = items.filter((c) => selected.has(c.candidate_id))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900 flex items-center">
            <Compass className="h-7 w-7 mr-2 text-primary-600" />
            Discovery
          </h2>
          <p className="text-secondary-600 mt-1">
            New developments found on the broker websites. Nothing becomes a project until you approve it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary flex items-center" onClick={refreshAll} disabled={list.isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${list.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button className="btn-primary flex items-center" onClick={() => runMutation.mutate({ baseline: false })} disabled={runMutation.isPending}>
            {runMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Run now
          </button>
        </div>
      </div>

      {/* Last run / sources */}
      <div className="bg-white border border-secondary-200 rounded-lg">
        <button className="w-full flex items-center justify-between px-4 py-3 text-left" onClick={() => setShowSources((v) => !v)}>
          <span className="text-sm text-secondary-700">
            Last run:{' '}
            <span className="font-medium text-secondary-900">{lastRun ? `${relative(lastRun.started_at)} (${lastRun.status})` : 'never'}</span>
            {failedHosts.length > 0 && (
              <span className="ml-3 text-red-600 font-medium">
                {failedHosts.length} source{failedHosts.length > 1 ? 's' : ''} failing
              </span>
            )}
            {counts && counts.auto_rejected > 0 && (
              <span className="ml-3 text-secondary-500">{counts.auto_rejected} resorts / branded rejected automatically</span>
            )}
          </span>
          {showSources ? <ChevronUp className="h-4 w-4 text-secondary-400" /> : <ChevronDown className="h-4 w-4 text-secondary-400" />}
        </button>
        {showSources && (
          <div className="border-t border-secondary-200 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-secondary-50 text-xs uppercase text-secondary-500">
                <tr>
                  <th className="text-left px-4 py-2">Broker</th>
                  <th className="text-left px-4 py-2">Site</th>
                  <th className="text-left px-4 py-2">Go-live</th>
                  <th className="text-left px-4 py-2">Last success</th>
                  <th className="text-right px-4 py-2">Developments</th>
                  <th className="text-left px-4 py-2">Last error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {(meta.data?.hosts ?? []).map((h) => (
                  <tr key={h.host}>
                    <td className="px-4 py-2 font-medium text-secondary-900">{h.broker_name ?? h.broker_id}</td>
                    <td className="px-4 py-2 text-secondary-600">{h.host}</td>
                    <td className="px-4 py-2 text-secondary-600">{h.baselined_at ? safeDate(h.baselined_at) : 'pending first run'}</td>
                    <td className="px-4 py-2 text-secondary-600">{relative(h.last_success_at)}</td>
                    <td className="px-4 py-2 text-right text-secondary-600">{h.last_item_count ?? '—'}</td>
                    <td className="px-4 py-2 text-red-600 max-w-xs" title={h.last_error ?? ''}>
                      <div className="truncate">{h.last_error ?? ''}</div>
                      {h.last_error?.startsWith('partial source') && (
                        <button
                          className="mt-1 text-xs underline text-secondary-600 hover:text-secondary-900"
                          disabled={runMutation.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Accept the smaller list from ${h.host} as the new normal? Developments it no longer shows will be marked as no longer listed, and anything new on it goes to "Before go-live".`,
                              )
                            ) {
                              runMutation.mutate({ baseline: true, hosts: [h.host] })
                            }
                          }}
                        >
                          Accept as baseline
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-secondary-100 text-xs text-secondary-500">
              Runs automatically every 5 days. Developments that already exist on a site at its first run go to “Before go-live”.
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="flex flex-wrap gap-1 -mb-px" aria-label="Discovery tabs">
          {TABS.map((t) => {
            const active = tab === t.key
            const n = countFor(t.key)
            return (
              <button
                key={t.key}
                title={t.hint}
                onClick={() => {
                  setTab(t.key)
                  setPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                {t.label}
                {n != null && n > 0 && (
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      active ? 'bg-primary-100 text-primary-700' : t.key === 'new' ? 'bg-red-100 text-red-700' : 'bg-secondary-100 text-secondary-600'
                    }`}
                  >
                    {n}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>
      <p className="text-sm text-secondary-500 -mt-3">{TABS.find((t) => t.key === tab)?.hint}</p>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
          <input
            className="form-input pl-9 w-64"
            placeholder="Search name, city or URL"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          className="form-input w-auto"
          value={brokerId}
          onChange={(e) => {
            setBrokerId(e.target.value ? Number(e.target.value) : '')
            setPage(1)
          }}
        >
          <option value="">All brokers</option>
          {brokers.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select className="form-input w-auto" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
          <option value="published_desc">Newest on broker site</option>
          <option value="seen_desc">Most recently discovered</option>
          <option value="name_asc">Name A–Z</option>
          <option value="broker_asc">Broker</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-secondary-700">
          <input type="checkbox" checked={onlyLive} onChange={(e) => { setOnlyLive(e.target.checked); setPage(1) }} />
          With units for sale
        </label>
        <label className="flex items-center gap-2 text-sm text-secondary-700">
          <input type="checkbox" checked={onlyDuplicates} onChange={(e) => { setOnlyDuplicates(e.target.checked); setPage(1) }} />
          Possible duplicates only
        </label>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2 text-sm">
          <span className="font-medium text-primary-800">{selected.size} selected</span>
          {tab !== 'done' && (
            <button className="btn-secondary !py-1 !px-3" onClick={() => setRejecting(selectedItems)}>
              Reject…
            </button>
          )}
          {tab !== 'snoozed' && tab !== 'done' && (
            <button className="btn-secondary !py-1 !px-3" onClick={() => bulkMutation.mutate('snooze')} disabled={bulkMutation.isPending}>
              Snooze 30 days
            </button>
          )}
          {['backlog', 'snoozed', 'rejected', 'gone'].includes(tab) && (
            <button className="btn-secondary !py-1 !px-3" onClick={() => bulkMutation.mutate('reopen')} disabled={bulkMutation.isPending}>
              Move to New
            </button>
          )}
          <button className="ml-auto text-secondary-500 underline" onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-secondary-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-secondary-200">
          <thead className="bg-secondary-50 text-xs uppercase tracking-wider text-secondary-500">
            <tr>
              <th className="px-3 py-3 w-8">
                <input type="checkbox" checked={allOnPageSelected} onChange={toggleAll} aria-label="Select all on page" />
              </th>
              <th className="px-3 py-3 text-left">Development</th>
              <th className="px-3 py-3 text-left">Broker</th>
              <th className="px-3 py-3 text-left">Location</th>
              <th className="px-3 py-3 text-left">Units</th>
              <th className="px-3 py-3 text-left">Published</th>
              <th className="px-3 py-3 text-left">Possible duplicate</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {list.isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-secondary-500">
                  Loading…
                </td>
              </tr>
            )}
            {list.isError && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-red-600">
                  {apiError(list.error).message}{' '}
                  <button className="underline" onClick={() => list.refetch()}>
                    Retry
                  </button>
                </td>
              </tr>
            )}
            {!list.isLoading && !list.isError && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-secondary-500">
                  {tab === 'new' ? 'Nothing waiting for approval.' : 'Nothing here.'}
                </td>
              </tr>
            )}
            {items.map((c) => (
              <tr key={c.candidate_id} className={selected.has(c.candidate_id) ? 'bg-primary-50/40' : undefined}>
                <td className="px-3 py-3 align-top">
                  <input
                    type="checkbox"
                    checked={selected.has(c.candidate_id)}
                    onChange={() => toggleOne(c.candidate_id)}
                    aria-label={`Select ${c.name ?? 'development'}`}
                  />
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="flex items-start gap-1.5">
                    <span className={`text-sm font-medium ${c.name ? 'text-secondary-900' : 'italic text-secondary-400'}`}>
                      {c.name ?? 'Name pending'}
                    </span>
                    <a href={c.canonical_url} target="_blank" rel="noreferrer" className="text-secondary-400 hover:text-primary-600" title={c.canonical_url}>
                      <ExternalLink className="h-3.5 w-3.5 mt-0.5" />
                    </a>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.status_raw && <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600">{c.status_raw}</span>}
                    {c.flags
                      .filter((f) => FLAG_LABELS[f])
                      .map((f) => (
                        <span key={f} className={`text-xs px-1.5 py-0.5 rounded ${FLAG_LABELS[f].tone}`}>
                          {FLAG_LABELS[f].label}
                        </span>
                      ))}
                    {!c.scrapable && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">Not scrapable</span>}
                    {c.review_reason && ['pending', 'snoozed', 'rejected', 'archived', 'approved', 'linked'].includes(c.review_status) && REASON_LABELS[c.review_reason] && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-500">{REASON_LABELS[c.review_reason]}</span>
                    )}
                    {c.review_status === 'snoozed' && c.snoozed_until && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-500">until {safeDate(c.snoozed_until)}</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 align-top text-sm text-secondary-700">{c.broker_name}</td>
                <td className="px-3 py-3 align-top text-sm text-secondary-700">
                  {[c.city, c.district].filter(Boolean).join(', ') || <span className="text-secondary-400">—</span>}
                  {c.neighbourhood && <div className="text-xs text-secondary-500">{c.neighbourhood}</div>}
                </td>
                <td className="px-3 py-3 align-top text-sm text-secondary-700 whitespace-nowrap">
                  {c.total_fractions != null ? (
                    <span className={c.available_fractions === 0 ? 'text-secondary-400' : ''}>
                      {c.available_fractions ?? '?'} / {c.total_fractions}
                    </span>
                  ) : c.is_live === true ? (
                    <span className="text-green-700">for sale</span>
                  ) : c.is_live === false ? (
                    <span className="text-secondary-400">none</span>
                  ) : (
                    <span className="text-secondary-400">—</span>
                  )}
                </td>
                <td className="px-3 py-3 align-top text-sm text-secondary-700 whitespace-nowrap">
                  {safeDate(c.published_at)}
                  <div className="text-xs text-secondary-400">found {relative(c.first_seen_at)}</div>
                </td>
                <td className="px-3 py-3 align-top text-sm">
                  {c.match_project_id ? (
                    <div>
                      <Link to={`/projects/${c.match_project_id}`} className="text-primary-700 hover:underline" target="_blank">
                        {c.match_name}
                      </Link>
                      <div className="text-xs text-secondary-500">
                        {c.match_broker_name ?? 'no broker'}
                        {c.match_sold_out ? ' · sold out' : ''} ·{' '}
                        {c.match_reason === 'name_exact'
                          ? 'same name'
                          : c.match_reason === 'republish_pair'
                            ? 'old page gone: re-published?'
                            : `similar name${c.match_score ? ` (${Math.round(c.match_score * 100)}%)` : ''}`}
                      </div>
                    </div>
                  ) : (
                    <span className="text-secondary-300">—</span>
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="flex items-center justify-end gap-1.5">
                    {canApprove(c) && (
                      <button
                        className="text-xs px-3 py-1.5 rounded bg-green-100 text-green-800 hover:bg-green-200 flex items-center"
                        onClick={() => setApproving(c)}
                        title="Create the project"
                      >
                        <Check className="h-3.5 w-3.5 mr-1" />
                        Approve
                      </button>
                    )}
                    {canApprove(c) && c.match_project_id && (
                      <button
                        className="text-xs px-2 py-1.5 rounded bg-blue-100 text-blue-800 hover:bg-blue-200"
                        onClick={() => setLinking(c)}
                        title="Same development already in SDI: link and update its URL"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {c.review_status === 'pending' && (
                      <button
                        className="text-xs px-2 py-1.5 rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                        onClick={() => actionMutation.mutate({ id: c.candidate_id, action: 'snooze' })}
                        title="Snooze for 30 days"
                        disabled={actionMutation.isPending}
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {canReject(c) && (
                      <button
                        className="text-xs px-2 py-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200"
                        onClick={() => setRejecting([c])}
                        title="Reject"
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {canReopen(c) && (
                      <button
                        className="text-xs px-2 py-1.5 rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200 flex items-center"
                        onClick={() => actionMutation.mutate({ id: c.candidate_id, action: 'reopen' })}
                        title="Move back to New"
                        disabled={actionMutation.isPending}
                      >
                        <Undo2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {c.project_id && (
                      <Link to={`/projects/${c.project_id}`} className="text-xs px-3 py-1.5 rounded bg-primary-100 text-primary-800 hover:bg-primary-200">
                        Open project
                      </Link>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-200 text-sm text-secondary-600">
            <span>
              {total} developments · page {page} of {pages}
            </span>
            <div className="flex gap-2">
              <button className="btn-secondary !py-1 !px-3" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </button>
              <button className="btn-secondary !py-1 !px-3" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {approving && (
        <ApproveModal
          candidate={approving}
          onClose={() => setApproving(null)}
          onDone={() => {
            setApproving(null)
            refreshAll()
            queryClient.invalidateQueries({ queryKey: ['projects'] })
          }}
        />
      )}
      {linking && (
        <LinkModal
          candidate={linking}
          onClose={() => setLinking(null)}
          onDone={() => {
            setLinking(null)
            refreshAll()
          }}
        />
      )}
      {rejecting && (
        <RejectModal
          candidates={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null)
            refreshAll()
          }}
        />
      )}
    </div>
  )
}
